import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import enCommon from '../locales/en-US/common.json';
import esCommon from '../locales/es-MX/common.json';
import frCommon from '../locales/fr-CA/common.json';

/**
 * The three MVP languages per [DDR-PLT-013](../../../../docs/ddr/Platform/DDR-PLT-013-localization-three-languages.md).
 * `en-US` is the canonical fallback when a key is missing in another bundle.
 *
 * Adding a fourth language (e.g., `en-CA`, `pt-BR`) is additive: drop the
 * bundle under `src/locales/<code>/`, add it here, and seed the matching
 * NotificationTemplate rows on the back end.
 */
export const SupportedLanguages = ['en-US', 'es-MX', 'fr-CA'] as const;
export type SupportedLanguage = (typeof SupportedLanguages)[number];

export const DefaultLanguage: SupportedLanguage = 'en-US';

/**
 * Resource map - eagerly imported so the SPA renders without a network round
 * trip and so the bundle-parity test can read every key set synchronously.
 * Module-owned namespaces (`insights`, `requests`, ...) land alongside their
 * feature blocks and register themselves via `i18n.addResourceBundle`.
 */
const resources = {
  'en-US': { common: enCommon },
  'es-MX': { common: esCommon },
  'fr-CA': { common: frCommon },
} as const;

/**
 * Order matters: `localStorage` first so a user's explicit choice survives a
 * reload, then the browser's `navigator.language`, finally the configured
 * default. `User.PreferredLanguage` is layered on top of this once IDN ships
 * via the `useUserLanguage()` hook in `src/i18n/useUserLanguage.ts`.
 */
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: [...SupportedLanguages],
    fallbackLng: DefaultLanguage,
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'ami.language',
    },
    saveMissing: true,
    missingKeyHandler: (lngs, ns, key) => {
      // Per architecture §5.6 - missing keys fall back to en-US with a
      // structured warning so gaps are auditable. The console warning becomes
      // an Application Insights `trackTrace` once block 008+'s telemetry
      // hook ships for the SPA.
      console.warn('i18n.missingKey', { languages: lngs, namespace: ns, key });
    },
  });

export { i18n };
